import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dog,
  Cat,
  Rabbit,
  ShieldAlert,
  Printer,
  Check,
  X,
  BadgeCheck,
  FileSignature,
  PenLine,
  HeartPulse,
  Phone,
  Stethoscope,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { BeatShell, fmt1 } from "@/components/nutrition/NutritionParts";
import { WARDS, HOUSING } from "@/data/admission";

const WARD_ICON = { general: Dog, cat: Cat, exotics: Rabbit, isolation: ShieldAlert };

/* deterministic barcode pattern from the patient id */
const Barcode = ({ seed }) => {
  const bars = useMemo(() => {
    let n = 0;
    for (let i = 0; i < seed.length; i += 1) n = (n * 31 + seed.charCodeAt(i)) % 100000;
    return Array.from({ length: 34 }, (_, i) => {
      n = (n * 1103515245 + 12345) % 2147483648;
      return 1 + ((n >> (i % 7)) % 3);
    });
  }, [seed]);
  return (
    <div className="flex items-end gap-[1px] h-7" aria-hidden="true">
      {bars.map((w, i) => (
        <span key={i} style={{ width: `${w}px` }} className={`h-full ${i % 2 ? "bg-slate-900" : "bg-transparent"}`} />
      ))}
    </div>
  );
};

/* ── Wristband + kennel-board housing beat (replaces the old HouseBeat) ─────── */
export function HousingBeat({ c, onDone }) {
  const h = HOUSING[c.id] || { ward: "general", chips: [] };
  const [ward, setWard] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [printed, setPrinted] = useState(false);
  const [res, setRes] = useState(null);
  const checked = !!res;
  const idNo = useMemo(() => `RV-${String(Math.abs([...c.id].reduce((a, ch) => a * 33 + ch.charCodeAt(0), 7)) % 90000 + 10000)}`, [c.id]);

  const toggle = (i) => {
    if (printed) return;
    setSel((prev) => {
      const n = new Set(prev);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  };

  const grade = () => {
    const wardPts = ward === h.ward ? 8 : 0;
    const trueIdx = h.chips.map((x, i) => (x.flag ? i : -1)).filter((i) => i >= 0);
    const caught = trueIdx.filter((i) => sel.has(i)).length;
    const falseTaps = [...sel].filter((i) => !h.chips[i].flag).length;
    const warnPts = Math.max(0, Math.min(7, Math.round((7 * caught) / (trueIdx.length || 1)) - 3 * falseTaps));
    return {
      earned: wardPts + warnPts,
      max: 15,
      detail: `Ward ${ward === h.ward ? "correct" : "review"} · ${caught}/${trueIdx.length} warnings, ${falseTaps} wrong`,
      key: "house",
      label: "Prepare & house",
    };
  };

  const onPrimary = () => {
    if (!checked) {
      if (!printed) return toast.error("Build and print the ID wristband first.");
      if (ward == null) return toast.error("Place the patient on the kennel board.");
      setRes(grade());
    } else {
      onDone(res);
    }
  };

  const chosenWarnings = h.chips.filter((_, i) => sel.has(i));

  return (
    <BeatShell
      Icon={BadgeCheck}
      eyebrow="Stage 4 of 5 · Prepare & house"
      title="Patient ID & housing"
      subtitle="Build the ID wristband (add the warnings that belong on it), print it, then place the patient on the correct kennel board."
      primaryLabel={checked ? "Continue →" : "Confirm housing"}
      onPrimary={onPrimary}
      primaryTestId="house-primary-btn"
    >
      {/* ── Wristband builder ── */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">1 · ID wristband</div>
        <div
          className={`rounded-xl border-2 p-3 transition-colors ${printed ? "border-emerald-400 bg-emerald-50/40" : "border-[#0047AB]/30 bg-[#0047AB]/5"}`}
          data-testid="wristband"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#0047AB]">Riverside Vet · Patient ID</span>
            <span className="text-[10px] font-mono-clinical text-slate-500">{idNo}</span>
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
            <div><span className="text-slate-400 text-[11px] uppercase tracking-wide">Patient</span> <span className="font-bold text-slate-900">{c.name}</span></div>
            <div><span className="text-slate-400 text-[11px] uppercase tracking-wide">Weight</span> <span className="font-mono-clinical font-semibold text-slate-900">{fmt1(c.preop?.scale)} kg</span></div>
            <div className="col-span-2"><span className="text-slate-400 text-[11px] uppercase tracking-wide">Procedure</span> <span className="font-semibold text-slate-800">{c.procedure}</span></div>
          </div>
          {chosenWarnings.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="wristband-warnings">
              {chosenWarnings.map((w) => (
                <span key={w.t} className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-rose-600 text-white">
                  {w.t}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
            <Barcode seed={c.id} />
            {printed && (
              <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-600" data-testid="wristband-applied">
                <BadgeCheck className="w-4 h-4" /> APPLIED
              </span>
            )}
          </div>
        </div>

        {/* warning chips */}
        <div className="text-[11px] text-slate-500 mt-3 mb-1.5">Tap the alerts that should be printed on the band:</div>
        <div className="flex flex-wrap gap-2">
          {h.chips.map((chip, i) => {
            const on = sel.has(i);
            let cls = "border-slate-300 bg-white text-slate-700 hover:border-[#0047AB]";
            if (checked) {
              if (chip.flag) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
              else if (on) cls = "border-rose-400 bg-rose-50 text-rose-700 line-through";
              else cls = "border-slate-200 bg-slate-50 text-slate-400";
            } else if (on) cls = "border-[#0047AB] bg-[#0047AB]/10 text-[#0047AB]";
            return (
              <button
                key={chip.t}
                type="button"
                disabled={printed}
                onClick={() => toggle(i)}
                data-testid={`house-warning-${i}`}
                className={`text-xs font-semibold rounded-full border-2 px-3 py-1.5 transition-all disabled:cursor-not-allowed ${cls}`}
              >
                {checked && chip.flag && "⚑ "}
                {chip.t}
              </button>
            );
          })}
        </div>

        {!printed && (
          <button
            type="button"
            onClick={() => setPrinted(true)}
            data-testid="print-wristband-btn"
            className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-white bg-[#0047AB] hover:bg-[#0a3f8f] rounded-md px-3.5 py-2 transition-colors"
          >
            <Printer className="w-4 h-4" /> Print & apply wristband
          </button>
        )}
      </div>

      {/* ── Kennel board ── */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">2 · Kennel board · place the patient</div>
        <div className="grid grid-cols-2 gap-2.5" data-testid="kennel-board">
          {WARDS.map((w) => {
            const Icon = WARD_ICON[w.id];
            const selected = ward === w.id;
            const isCorrect = w.id === h.ward;
            let cls = "border-slate-200 bg-white hover:border-[#0047AB]/60";
            if (checked) {
              if (isCorrect) cls = "border-emerald-400 bg-emerald-50";
              else if (selected) cls = "border-rose-400 bg-rose-50";
              else cls = "border-slate-200 bg-white opacity-60";
            } else if (selected) cls = "border-[#0047AB] bg-[#0047AB]/5 ring-1 ring-[#0047AB]/30";
            return (
              <button
                key={w.id}
                type="button"
                disabled={checked}
                onClick={() => setWard(w.id)}
                data-testid={`kennel-${w.id}`}
                className={`text-left border-2 rounded-lg p-3 transition-all ${cls}`}
              >
                <div className="flex items-center justify-between">
                  <Icon className="w-5 h-5 text-slate-600" />
                  {selected && !checked && <span className="w-4 h-4 rounded-full bg-[#0047AB]" />}
                  {checked && isCorrect && <Check className="w-4 h-4 text-emerald-600" />}
                  {checked && selected && !isCorrect && <X className="w-4 h-4 text-rose-500" />}
                </div>
                <div className="text-sm font-bold text-slate-900 mt-1.5">{w.label}</div>
                <div className="text-[11px] text-slate-500">{w.sub}</div>
              </button>
            );
          })}
        </div>
        {checked && (
          <div className="text-[11px] text-slate-500 mt-2" data-testid="kennel-key">
            Correct ward: <span className="font-semibold text-emerald-700">{WARDS.find((w) => w.id === h.ward)?.label}</span> — {h.why}
          </div>
        )}
      </div>
    </BeatShell>
  );
}

/* ── Signable consent sheet (gate inside ConsentBeat) ───────────────────────── */
export function ConsentSheet({ c, signed, onSign }) {
  const items = [
    `I confirm the procedure: ${c.procedure}`,
    "I understand the anaesthetic / sedation risks explained",
    "I consent to pre-anaesthetic blood testing (PABT)",
    "I authorise the written estimate and costs",
    "A daytime contact number has been provided",
    "An alternative / emergency contact number has been provided",
  ];
  const [ticks, setTicks] = useState(items.map(() => false));
  const [name, setName] = useState("");
  const [dnr, setDnr] = useState("");
  const allTicked = ticks.every(Boolean) && name.trim().length >= 2 && dnr !== "";

  const toggle = (i) => signed || setTicks((p) => p.map((v, idx) => (idx === i ? !v : v)));

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="consent-sheet">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#0047AB] font-semibold mb-2">
        <FileSignature className="w-3.5 h-3.5" /> Consent &amp; authorisation form
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <label
            key={i}
            data-testid={`consent-tick-${i}`}
            className={`flex items-start gap-2.5 text-sm rounded-md px-2 py-1.5 cursor-pointer ${ticks[i] ? "bg-white" : ""}`}
          >
            <span className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${ticks[i] ? "border-[#0047AB] bg-[#0047AB]" : "border-slate-300"}`}>
              {ticks[i] && <Check className="w-3 h-3 text-white" />}
            </span>
            <input type="checkbox" className="sr-only" checked={ticks[i]} onChange={() => toggle(i)} disabled={signed} />
            <span className="text-slate-700">{it}</span>
          </label>
        ))}
      </div>

      {/* Resuscitation / DNR directive */}
      <div className="mt-3 border-t border-slate-200 pt-3" data-testid="dnr-directive">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#0047AB] font-semibold mb-2">
          <HeartPulse className="w-3.5 h-3.5" /> If resuscitation is needed (CPR / DNR directive)
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            {
              id: "owner",
              label: "Call the owner first",
              desc: "Contact me before any resuscitation decision is made",
              Icon: Phone,
            },
            {
              id: "vet",
              label: "Allow the vet to decide",
              desc: "I authorise the attending vet to act in the patient's best interest",
              Icon: Stethoscope,
            },
          ].map((opt) => {
            const active = dnr === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={signed}
                onClick={() => setDnr(opt.id)}
                data-testid={`dnr-option-${opt.id}`}
                className={`text-left flex items-start gap-2.5 rounded-md border-2 px-3 py-2 transition-colors disabled:cursor-not-allowed ${
                  active
                    ? "border-[#0047AB] bg-[#0047AB]/5"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <span
                  className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    active ? "border-[#0047AB]" : "border-slate-300"
                  }`}
                >
                  {active && <span className="w-2 h-2 rounded-full bg-[#0047AB]" />}
                </span>
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                    <opt.Icon className="w-3.5 h-3.5 text-slate-500" /> {opt.label}
                  </span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">{opt.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Owner signature</div>
          <div className="flex items-center gap-2 border-b-2 border-slate-300 pb-1">
            <PenLine className="w-4 h-4 text-slate-400" />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={signed}
              placeholder="Type owner's full name"
              data-testid="consent-signature"
              className={`border-0 bg-transparent px-0 h-7 focus-visible:ring-0 ${signed ? "font-caveat text-xl text-[#0047AB]" : ""}`}
            />
          </div>
        </div>
        <button
          type="button"
          disabled={!allTicked || signed}
          onClick={() => onSign(true)}
          data-testid="sign-consent-btn"
          className="flex items-center gap-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-3.5 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FileSignature className="w-4 h-4" /> {signed ? "Signed & authorised" : "Sign & authorise"}
        </button>
      </div>
    </div>
  );
}
